// src/components/NotificationList.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function NotificationList({ notifications, fetchNotifications, authAxios, setShowNotificationsModal, navigateToItem }) {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPanel, setShowPanel] = useState(true);

  // Function to mark a single notification as read
  const markNotificationAsRead = useCallback(async (notificationId) => {
    try {
      await authAxios.post(`notifications/${notificationId}/mark_as_read/`);
      fetchNotifications(); // Re-fetch notifications to update the UI
    } catch (error) {
      console.error("Error marking notification as read:", error);
      // Optionally, show an error message to the user
    }
  }, [authAxios, fetchNotifications]);

  // Function to mark all unread notifications as read
  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await authAxios.post('notifications/mark_all_as_read/');
      fetchNotifications(); // Re-fetch notifications to update the UI
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      // Optionally, show an error message to the user
    }
  }, [authAxios, fetchNotifications]);

  // Close the modal
  const closeModal = useCallback(() => {
    setShowNotificationsModal(false);
  }, [setShowNotificationsModal]);

  const navigate = useNavigate();
  const panelRef = useRef(null);

  // Handle clicking a notification: mark as read (if needed) then open in-place modal
  const handleNotificationClick = useCallback(async (notification) => {
    try {
      if (!notification.is_read) {
        await authAxios.post(`notifications/${notification.id}/mark_as_read/`);
        fetchNotifications();
      }
      // Hide the dropdown panel locally (keep component mounted) and open detail modal in-place
      setShowPanel(false);
      setSelectedNotification(notification);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  }, [authAxios, fetchNotifications]);

  // Close when clicking outside the panel or pressing Escape
  useEffect(() => {
    function handleOutsideClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        // If the detail modal is open, close it; otherwise close the dropdown entirely
        if (showDetailModal) {
          setShowDetailModal(false);
          setSelectedNotification(null);
        } else {
          closeModal();
        }
      }
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        if (showDetailModal) {
          setShowDetailModal(false);
          setSelectedNotification(null);
        } else {
          closeModal();
        }
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);

    // focus the panel for keyboard users
    if (panelRef.current && typeof panelRef.current.focus === 'function') {
      try { panelRef.current.focus(); } catch (err) { /* ignore */ }
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [panelRef, closeModal, showDetailModal]);

  const closeDetailModal = useCallback(() => {
    setShowDetailModal(false);
    setSelectedNotification(null);
  }, []);

  const goToItem = useCallback(() => {
    if (!selectedNotification) return;

    // Priority 1: use navigateToItem if entity_type & entity_id present
    if (navigateToItem && selectedNotification.entity_type && selectedNotification.entity_id) {
      // Map backend entity_type to existing navigateToItem resource.type values
      const map = {
        'task': 'task_due',
        'invoice': 'invoice_due',
        'case': 'case_close',
        'communicationlog': 'communication_log',
        'communication_log': 'communication_log',
        'document': 'document',
        'staffprofile': 'attorney_records'
      };
      const rawType = selectedNotification.entity_type.toLowerCase();
      const mappedType = map[rawType];
      if (mappedType) {
  navigateToItem({ type: mappedType, id: selectedNotification.entity_id, autoOpen: true });
        setShowDetailModal(false);
        setSelectedNotification(null);
        setShowNotificationsModal(false);
        setShowPanel(true);
        return;
      }
    }

    // Fallback 2: legacy URL navigation (internal relative)
    const raw = selectedNotification.url || '';
    if (raw) {
      const isAbsolute = /^https?:\/\//i.test(raw);
      const normalized = isAbsolute ? raw : (raw.startsWith('/') ? raw : `/${raw}`);
      if (isAbsolute) {
        window.location.assign(normalized);
      } else {
        navigate(normalized, { state: { fromNotification: true } });
      }
    }

    setShowDetailModal(false);
    setSelectedNotification(null);
    setShowNotificationsModal(false);
    setShowPanel(true);
  }, [navigate, navigateToItem, selectedNotification, setShowNotificationsModal]);

  const canGoTo = !!(selectedNotification && ((selectedNotification.entity_type && selectedNotification.entity_id) || selectedNotification.url));

  return (
    // Tailwind dropdown panel anchored near the notification icon; no global animations
    <div className="z-[9999]">
      <div className="">
        {showPanel && (
        <div className="fixed right-4 top-16 w-96">
          <div ref={panelRef} tabIndex={-1} className="bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 rounded-lg shadow-lg overflow-hidden">
            <div className="px-4 py-2 flex items-center justify-between border-b dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
              <div className="flex items-center gap-2">
                <button
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-300"
                  onClick={markAllNotificationsAsRead}
                  disabled={notifications.filter(notif => !notif.is_read).length === 0}
                >
                  Mark all
                </button>
                <button
                  onClick={closeModal}
                  aria-label="Close notifications"
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-md"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="max-h-72 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">No notifications</div>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {notifications.map(notification => (
                    <li
                      key={notification.id}
                      className={`px-4 py-3 flex items-start justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer ${notification.is_read ? 'opacity-70' : 'bg-white dark:bg-gray-800'}`}
                      onClick={() => handleNotificationClick(notification)}
                      onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNotificationClick(notification); }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open notification: ${notification.message}`}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{(notification.notification_type ? notification.notification_type.replace(/_/g, ' ') : 'Notification')}</div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">{notification.message}</div>
                        <div className="text-xs text-gray-400 mt-1">{new Date(notification.created_at).toLocaleString()}</div>
                      </div>
                      {!notification.is_read && (
                        <button
                          className="text-xs text-blue-600 hover:underline ml-2"
                          onClick={(e) => { e.stopPropagation(); markNotificationAsRead(notification.id); }}
                          aria-label={`Mark notification ${notification.id} as read`}
                        >
                          Mark
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* In-place Notification Detail Modal */}
      {showDetailModal && selectedNotification && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeDetailModal}
            aria-hidden="true"
          />
          {/* Modal Panel */}
          <div
            role="dialog"
            aria-modal="true"
            className="relative bg-white dark:bg-gray-800 w-full max-w-lg mx-4 rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 transform transition-all"
          >
            <div className="px-5 py-4 border-b dark:border-gray-700 flex items-center justify-between">
              <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100">Notification</h4>
              <button
                onClick={closeDetailModal}
                aria-label="Close notification details"
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md"
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {(selectedNotification.notification_type ? selectedNotification.notification_type.replace(/_/g, ' ') : 'Notification')}
              </div>
              <div className="text-gray-900 dark:text-gray-100 text-sm whitespace-pre-line">
                {selectedNotification.message}
              </div>
              <div className="text-xs text-gray-400 mt-3">
                {new Date(selectedNotification.created_at).toLocaleString()}
              </div>
              {selectedNotification.entity_type && selectedNotification.entity_id && (
                <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  Related: {selectedNotification.entity_type} #{selectedNotification.entity_id}
                </div>
              )}
            </div>
            <div className="px-5 py-3 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-700 flex items-center justify-end gap-2">
              <button
                onClick={closeDetailModal}
                className="px-3 py-2 text-sm rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={goToItem}
                disabled={!canGoTo}
                className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Go to item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationList;
